<template>
  <section
    v-if="!isUnavailable"
    id="tiers"
    class="bg-gradient-to-b from-bg via-white to-white py-20 tablet:py-28"
  >
    <div class="container">
      <div class="mx-auto max-w-3xl text-center">
        <h2 class="text-3xl font-bold tracking-tight tablet:text-[44px] tablet:leading-[1.1]">
          {{ t('landing.tierLadder.title') }}
        </h2>
        <p class="mt-4 text-base text-black-80 tablet:text-lg">
          {{ t('landing.tierLadder.description') }}
        </p>
      </div>

      <div
        class="mx-auto mt-12 max-w-4xl rounded-3xl border border-black/[0.06] bg-white p-5 shadow-xl shadow-black/5 tablet:p-8"
      >
        <!-- Head -->
        <div class="flex flex-col gap-4 tablet:flex-row tablet:items-end tablet:justify-between">
          <div>
            <div class="text-[11px] font-bold uppercase tracking-[0.18em] text-gold-primary-120">
              {{ t('landing.tierLadder.eyebrow') }}
            </div>
            <i18n-t
              keypath="landing.tierLadder.subtitle"
              scope="global"
              tag="div"
              class="mt-1 text-2xl font-bold tracking-tight tablet:text-[28px]"
            >
              <template #accent
                ><span class="text-gold-primary-120">{{ t('landing.tierLadder.subtitleAccent') }}</span></template
              >
            </i18n-t>
            <p class="mt-1.5 max-w-md text-sm text-black-60">{{ t('landing.tierLadder.hint') }}</p>
          </div>
          <div
            class="inline-flex rounded-xl bg-black/[0.04] p-1"
            role="tablist"
            :aria-label="t('landing.tierLadder.dealSourceAriaLabel')"
          >
            <button
              v-for="m in ['self', 'ref']"
              :key="m"
              class="rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
              :class="mode === m ? 'bg-white text-black-100 shadow-sm' : 'text-black-60 hover:text-black-80'"
              role="tab"
              :aria-selected="mode === m"
              @click="setMode(m as 'self' | 'ref')"
            >
              {{ m === 'self' ? t('landing.tierLadder.modeSelf') : t('landing.tierLadder.modeRef') }}
            </button>
          </div>
        </div>

        <!-- Slider -->
        <div class="mt-6">
          <div class="mb-2 flex items-center justify-between">
            <span class="text-xs font-semibold uppercase tracking-wider text-black-60">{{
              t('landing.tierLadder.dealCommission')
            }}</span>
            <span class="text-lg font-bold text-black-100 tabular-nums">
              AED {{ formatNumber(deal)
              }}<span class="ms-1 text-xs font-medium text-black-60">{{ t('landing.tierLadder.gross') }}</span>
            </span>
          </div>
          <input
            v-model.number="deal"
            type="range"
            :min="DEAL_MIN_AED"
            :max="DEAL_MAX_AED"
            :step="DEAL_STEP_AED"
            class="calc-slider h-2 w-full appearance-none rounded-full"
            :style="{ '--pct': sliderPct + '%' }"
            :aria-label="t('landing.tierLadder.sliderAriaLabel')"
            @input="trackFirstUse"
          />
          <div class="mt-1.5 flex justify-between text-[10px] text-black-60">
            <span>20K</span><span>250K</span><span>500K</span><span>750K</span><span>1M</span><span>1.5M</span>
          </div>
        </div>

        <!-- Note -->
        <div class="mt-5 flex items-start gap-3 rounded-xl bg-gold-primary-10 p-4 text-sm text-black-80">
          <svg
            viewBox="0 0 24 24"
            class="mt-0.5 size-4 shrink-0 text-gold-primary-120"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
            />
            <path
              d="M12 16v-4M12 8h.01"
              stroke-linecap="round"
            />
          </svg>
          <div>
            <i18n-t
              v-if="mode === 'self'"
              keypath="landing.tierLadder.selfNote"
              scope="global"
            >
              <template #lead
                ><b>{{ t('landing.tierLadder.selfNoteLead') }}</b></template
              >
            </i18n-t>
            <i18n-t
              v-else
              keypath="landing.tierLadder.refNote"
              scope="global"
            >
              <template #lead
                ><b>{{ t('landing.tierLadder.refNoteLead') }}</b></template
              >
            </i18n-t>
          </div>
        </div>

        <!-- Chart -->
        <div
          v-if="!rows.length"
          class="mt-6 space-y-2"
          aria-busy="true"
        >
          <div
            v-for="n in SKELETON_ROWS"
            :key="n"
            class="grid items-center gap-3 rounded-xl px-3 py-2.5 tablet:grid-cols-[110px_1fr_140px] tablet:gap-[18px] tablet:px-3.5"
          >
            <div class="space-y-1.5">
              <div class="h-3.5 w-16 animate-pulse rounded bg-black/[0.06]" />
              <div class="h-2.5 w-24 animate-pulse rounded bg-black/[0.04]" />
            </div>
            <div class="h-2.5 animate-pulse rounded-full bg-black/[0.05]" />
            <div class="ms-auto h-3.5 w-24 animate-pulse rounded bg-black/[0.06]" />
          </div>
        </div>
        <div
          v-else
          class="mt-6 space-y-2"
        >
          <div
            v-for="(row, i) in rows"
            :key="row.tierKey"
            class="grid cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 transition-colors tablet:grid-cols-[110px_1fr_140px] tablet:gap-[18px] tablet:px-3.5 [&>*]:min-w-0"
            :class="activeIdx === i ? 'bg-gold-primary-10' : 'hover:bg-black/[0.02]'"
            @click="activeIdx = i"
          >
            <div>
              <div class="text-sm font-bold text-black-100">{{ t(row.labelKey) }}</div>
              <div class="text-[11px] text-black-60">{{ row.percent }}% · {{ row.range }}</div>
            </div>
            <div class="relative h-2.5 overflow-visible rounded-full bg-black/[0.05]">
              <div
                class="absolute inset-y-0 start-0 rounded-full bg-gradient-to-r from-gold-primary-100 rtl:bg-gradient-to-l to-gold-primary-120 transition-[width] duration-300"
                :style="{ width: row.widthPct + '%' }"
              ></div>
              <div
                class="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white ring-2 ring-gold-primary-120 transition-[inset-inline-start] duration-300 rtl:translate-x-1/2"
                :style="{ insetInlineStart: row.widthPct + '%' }"
              ></div>
            </div>
            <div class="text-end text-sm font-bold text-black-100 tabular-nums">
              <span class="me-1 text-[10px] font-medium uppercase tracking-wider text-black-60">AED</span
              >{{ formatNumber(row.payout) }}
            </div>
          </div>
        </div>
        <p class="mt-4 flex items-center gap-1.5 text-sm text-black-80">
          <span
            aria-hidden="true"
            class="before:content-['⚡']"
          />{{ t('landing.tierLadder.fastPayProTeam') }}
        </p>
      </div>

      <!-- Skip note -->
      <div
        class="mx-auto mt-8 flex max-w-3xl flex-col items-start gap-4 rounded-2xl border border-gold-primary-100 bg-gold-primary-10 p-5 tablet:flex-row tablet:items-center"
      >
        <div class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white text-gold-primary-120">
          <svg
            viewBox="0 0 24 24"
            class="size-5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
          </svg>
        </div>
        <i18n-t
          keypath="landing.tierLadder.skipNote"
          scope="global"
          tag="div"
          class="flex-1 text-sm text-black-80"
        >
          <template #lead
            ><b>{{ t('landing.tierLadder.skipNoteLead') }}</b></template
          >
        </i18n-t>
        <NuxtLink
          :to="applyHref"
          @click="trackCtaClick('tiers')"
          class="inline-flex items-center gap-2 rounded-lg bg-black-100 px-4 py-2.5 text-sm font-semibold text-white"
        >
          {{ t('landing.tierLadder.contactAm') }}
          <svg
            viewBox="0 0 24 24"
            class="size-4 rtl:-scale-x-100"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { formatTierRange, tierPayoutAed } from '~/utils/tiers/ladderView';

const DEAL_MIN_AED = 20_000;
const DEAL_MAX_AED = 1_500_000;
const DEAL_STEP_AED = 10_000;
const DEAL_DEFAULT_AED = 150_000;
const SKELETON_ROWS = 6;

const { t } = useI18n();
const { formatNumber } = useFormat();
const route = useRoute();
const { sendEventToBackend } = useTracking();
const { ladder, isUnavailable } = useCommissionTiersMap();

const mode = ref<'self' | 'ref'>('self');
const deal = ref(DEAL_DEFAULT_AED);
const activeIdx = ref(2);

const rows = computed(() => {
  const withPayout = ladder.value.map((tier) => {
    const percent = mode.value === 'self' ? tier.selfClosePercent : tier.referralPercent;
    return {
      tierKey: tier.tierKey,
      labelKey: `landing.tierLadder.tiers.${tier.tierKey.toLowerCase()}`,
      range: formatTierRange(tier.minCommissionAed, tier.maxCommissionAed),
      percent,
      payout: tierPayoutAed(deal.value, percent),
    };
  });
  const maxPayout = Math.max(1, ...withPayout.map((r) => r.payout));
  return withPayout.map((r) => ({ ...r, widthPct: (r.payout / maxPayout) * 100 }));
});

const sliderPct = computed(() => ((deal.value - DEAL_MIN_AED) / (DEAL_MAX_AED - DEAL_MIN_AED)) * 100);

const { signupHref: applyHref } = useSignupHref();
const { trackCtaClick } = useLandingTracking();

let hasTrackedUse = false;
const trackFirstUse = () => {
  if (hasTrackedUse) return;
  hasTrackedUse = true;
  void sendEventToBackend('tier_calculator_used', {
    mode: mode.value === 'self' ? 'self' : 'referral',
    dealCommissionAed: deal.value,
    page: route.path.replace(/\/$/, '').endsWith('/agents') ? 'agents' : 'home',
  });
};

const setMode = (next: 'self' | 'ref') => {
  if (mode.value === next) return;
  mode.value = next;
  trackFirstUse();
};
</script>

<style scoped lang="scss">
.calc-slider {
  background: linear-gradient(to right, #eda436 0%, #eda436 var(--pct, 10%), #00000010 var(--pct, 10%), #00000010 100%);

  // A native range input runs right-to-left under RTL — fill from that side too.
  [dir='rtl'] & {
    background: linear-gradient(
      to left,
      #eda436 0%,
      #eda436 var(--pct, 10%),
      #00000010 var(--pct, 10%),
      #00000010 100%
    );
  }
}
.calc-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #eda436;
  box-shadow: 0 2px 6px rgba(237, 164, 54, 0.35);
  cursor: pointer;
}
.calc-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #eda436;
  cursor: pointer;
}
</style>
